<template>
  <div class="relative">
    <!-- Кнопка пользователя -->
    <button
      class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
      @click="toggleMenu"
    >
      <!-- Аватар пользователя -->
      <div class="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
        <img
          v-if="authStore.user?.avatar"
          :src="authStore.user.avatar"
          :alt="authStore.userName"
          class="w-full h-full object-cover"
        />
        <UserIcon v-else class="w-5 h-5 text-gray-500" />
      </div>
      
      <!-- Имя пользователя (скрывается на мобильных) -->
      <span class="hidden sm:block text-sm font-medium text-gray-700">
        {{ authStore.userName }}
      </span>
      
      <!-- Иконка стрелки -->
      <ChevronDownIcon
        :class="[
          'w-4 h-4 text-gray-500 transition-transform',
          isMenuOpen ? 'rotate-180' : ''
        ]"
      />
    </button>

    <!-- Выпадающее меню -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isMenuOpen"
        class="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
      >
        <!-- Информация о пользователе -->
        <div class="px-4 py-3 border-b border-gray-100">
          <p class="text-sm font-medium text-gray-900">{{ authStore.userName }}</p>
          <p class="text-sm text-gray-500">{{ authStore.userEmail }}</p>
        </div>

        <!-- Пункты меню -->
        <div class="py-1">
          <router-link
            to="/account/dashboard"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="closeMenu"
          >
            <UserIcon class="w-4 h-4 mr-3 text-gray-400" />
            Dashboard
          </router-link>
          
          <router-link
            to="/account/settings"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="closeMenu"
          >
            <CogIcon class="w-4 h-4 mr-3 text-gray-400" />
            Settings
          </router-link>
          
          <router-link
            to="/account/billing"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="closeMenu"
          >
            <CreditCardIcon class="w-4 h-4 mr-3 text-gray-400" />
            Billing
          </router-link>
        </div>

        <div class="border-t border-gray-100 py-1">
          <button
            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="handleLogout"
          >
            <ArrowRightOnRectangleIcon class="w-4 h-4 mr-3 text-gray-400" />
            Sign Out
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  UserIcon,
  ChevronDownIcon,
  CogIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const authStore = useAuthStore()

// State
const isMenuOpen = ref(false)

// Methods
function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function closeMenu() {
  isMenuOpen.value = false
}

function handleLogout() {
  authStore.logout()
  closeMenu()
  router.push('/')
}

// Close menu when clicking outside
function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement
  const menuContainer = target.closest('.relative')
  if (!menuContainer) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script> 